import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, Stack, Modal, useMediaQuery } from "@mui/material";

import { useNavigate, Outlet } from "react-router-dom";
import { Paid } from '@mui/icons-material';

import { colorTheme, schoolDistrictLogos } from "../constants";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonSharpIcon from '@mui/icons-material/PersonSharp';
import MenuIcon from '@mui/icons-material/Menu';
import GroupIcon from '@mui/icons-material/Group';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import QuizIcon from '@mui/icons-material/Quiz';
import CreateIcon from '@mui/icons-material/Create';

import PlainModal from './modals/PlainModal';
import { schoolDistrictColors } from '../constants';
import withThisUser from "../wrappers/withThisUser";

import { SystemMessagePopup } from './shell';

import "../App.scss";

const MainLayout = ({thisUserData}) => {
  const navigate = useNavigate();

  const isMobile = useMediaQuery("(max-width:950px)");

  const isDistrictAdmin = thisUserData.userType === 'district_admin';

  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenuOpen = () => {isMobile && setMenuOpen(!menuOpen)};
  const [logOutOpen, setLogOutOpen] = useState(false);
  const handleLogOutOpen = () => setLogOutOpen(true);
  const handleLogOutClose = () => setLogOutOpen(false);

  const [token, setToken] = useState(localStorage.getItem("access_token"))
  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [userType, setUserType] = useState("");
  const [userSchoolDistrict, setUserSchoolDistrict] = useState("");
  const [examTokens, setExamTokens] = useState("");

  const theme = colorTheme.light;

  useEffect(()=> {
    if (!isMobile) {
      setMenuOpen(true);
    }
  },[isMobile]);

  useEffect(() => {
    setToken(localStorage.getItem("access_token"));
    if (token) {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/user-info/`,{
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setUserId(response.data.userId);
          setFirstName(response.data.firstName);
          setUserType(response.data.userType);
          setUserSchoolDistrict(response.data.schoolDistrictName);
          setExamTokens(response.data.examTokens);
          localStorage.setItem("exam_tokens", response.data.examTokens);
          setUsername(response.data.username);
        })
        .catch((error) => {
          console.error("Failed to fetch user info:", error);
        });
    }
  }, [token]);


  const logoutTitle = 'Log out?'
  const logoutSubText = 'Are you sure that you want to end your session?'
  const logoutButtonText = <Typography>Log out</Typography>
  const logoutButtonStyle = {bgcolor:"#EBEBEB", borderTopLeftRadius:0,borderTopRightRadius:0}

  const handleLogout = () => {
    axios.defaults.headers.common['Authorization'] = null;
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.clear();
    console.log('Local Storage cleared');
    navigate('/login', {replace:true});
  }
  const AppBarStyle = {width:'100%', color:'white', bgcolor:schoolDistrictColors[thisUserData.schoolDistrictName]};
  const bigBoxStyle = {width:'100%', display:'flex', flexDirection:'row', justifyContent:'space-between'};
  const mobileBoxStyle = {width:'100%', display:'flex', flexDirection:'row', justifyContent:'space-between'};


  const menuRowsStyle = {
    padding: isMobile ? '15px 20px 15px 20px' : '15px 40px 15px 20px',
    alignItems: 'center',
    cursor:'pointer',
    transition: "transform 02.s ease-in-out",
    "&:hover": {
      backgroundColor: theme.background_dark_grey,
    }
  };

  const iconStyle = {
    color: theme.text_light_grey, 
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "scale(1.1)"
    }
  };

  const menuTextStyle = {
    color: theme.text_light_grey
  }



  return (
    <Box>
      <Modal open={logOutOpen} onClose={handleLogOutClose}>
        <PlainModal headerString={'Log out'} actionString={'Are you sure you want to end your session?'} action={handleLogout} handleClose={handleLogOutClose}/>
      </Modal>
      <SystemMessagePopup />
      
    
      <Stack
        sx={{
          position: "fixed",
          left: 0,
          width: menuOpen ? "200px" : "0px",
          height: "100%",
          padding: 0,
          paddingTop: isMobile ? 0 : 2,
          zIndex: isMobile ? 1000 : 0,
          backgroundColor: theme.background_grey,
          justifyContent: "space-between",
          overflow: "hidden",
          transition: "width 0.3s ease",
          boxShadow: "4px 0 8px rgba(0,0,0,0.25)",
          
        }}
      >
        <Stack>
          <Stack
            sx={{
              alignItems:'flex-end',
              padding:'15px 20px 15px 20px'
            }}
          >
            <CloseIcon onClick={toggleMenuOpen} fontSize="large" sx={iconStyle} />
          </Stack>
          {isDistrictAdmin && (
            <Stack
              direction={'row'}
              spacing={1}
              sx={menuRowsStyle}
              onClick={() => {navigate("/statistics"); toggleMenuOpen()}}
            >
              <HomeIcon onClick={() => {navigate("/statistics"); toggleMenuOpen();}} fontSize="large" sx={iconStyle} />
              {<Typography variant='h6'sx={menuTextStyle}>Dashboard</Typography>}
            </Stack>
          )}

          {!isDistrictAdmin && (
            <Stack
              direction={'row'}
              spacing={1}
              sx={menuRowsStyle}
              onClick={() => {navigate("/exam-catalog"); toggleMenuOpen();}}
            >
              <QuizIcon onClick={() => {navigate("/exam-catalog"); toggleMenuOpen();}} fontSize="large" sx={iconStyle} />
              {<Typography variant='h6'sx={menuTextStyle}>Exams</Typography>}
            </Stack>
          )}


          <Stack
            direction={'row'}
            spacing={1}
            sx={menuRowsStyle}
            onClick={() => {navigate("/schedule"); toggleMenuOpen();}}
          >
            <CalendarMonthIcon onClick={() => navigate("/schedule")} fontSize="large" sx={iconStyle} />
            {<Typography variant='h6'sx={menuTextStyle}>Schedule</Typography>}
            
          </Stack>

          {isDistrictAdmin && (
            <Stack
              direction={'row'}
              spacing={1}
              sx={menuRowsStyle}
              onClick={() => {navigate("/users-all"); toggleMenuOpen();}}
            >
              <GroupIcon onClick={() => {navigate("/users-all"); toggleMenuOpen();}} fontSize="large" sx={iconStyle} />
              {<Typography variant='h6'sx={menuTextStyle}>Users</Typography>}
            </Stack>
          )}

          {isDistrictAdmin && (
            <Stack
              direction={'row'}
              spacing={1}
              sx={menuRowsStyle}
              onClick={() => {navigate("/exam-studio"); toggleMenuOpen();}}
            >
              <CreateIcon onClick={() => {navigate("/exam-studio"); toggleMenuOpen();}} fontSize="large" sx={iconStyle} />
              {<Typography variant='h6'sx={menuTextStyle}>Studio</Typography>}
            </Stack>
          )}

        </Stack>

        <Stack>
          <Stack
            direction={'row'}
            spacing={1}
            sx={menuRowsStyle}
            onClick={() => {navigate('/user-settings'); toggleMenuOpen();}}
          >
            <PersonSharpIcon onClick={() => {navigate('/user-settings'); toggleMenuOpen();}} fontSize="large" sx={iconStyle} />
            {<Typography variant='h6'sx={menuTextStyle}>Profile</Typography>}
          </Stack>
          <Stack
            direction={'row'}
            spacing={1}
            sx={menuRowsStyle}
            onClick={handleLogOutOpen}
          >
            <LogoutIcon onClick={handleLogOutOpen} fontSize="large" sx={iconStyle} />
            {<Typography variant='h6'sx={menuTextStyle}>Logout</Typography>}
          </Stack>
        </Stack>

      </Stack>
      

      <Box sx={{position:'fixed',width:'100%', zIndex:999}}>
        <Box sx={AppBarStyle}>
          <Box sx={{p:2, display:'flex', flexDirection:'row', justifyContent:'space-between', alignItems:'center'}}>
            <Stack direction={'row'} spacing={2}>
              {isMobile && (
                <MenuIcon
                  fontSize='large'
                  sx={{
                    color: "#EFEFEF", 
                    cursor: "pointer",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                        color: "#FFFFFF",
                        transform: "scale(1.1)"
                    }
                  }}
                  onClick={toggleMenuOpen}
                />
              )}
              <Typography variant="h5" sx={{fontWeight:600}}>
                Welcome {firstName}!
              </Typography>
              
              {!isMobile && (
                <Stack direction={'row'} spacing={2}>
                <Typography variant="h6">
                  ({username})
                </Typography>
                  <div className='token-count'><Paid /> {examTokens}</div>
                </Stack>
                
              )}
            </Stack>

            <Stack direction={'row'} sx={{alignItems:'center'}}>
              <Box
                component="img"
                src={schoolDistrictLogos[userSchoolDistrict]}
                alt="School District Logo"
                sx={{ width: 'auto', height: 50, maxWidth: "100%",mr:0}}
              />
              {!isMobile && (
                <Typography variant="h5" sx={{fontWeight:600, ml:2}}>
                  {userSchoolDistrict}
                </Typography>
              )}
              
            </Stack>
          </Box>
        </Box>
      </Box>
    
              
      <Box sx={{display:'flex',flexDirection:'column',alignItems:'center',pt:17, pr:2, pl:2, ml: !isMobile ? '200px' : 0}}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default withThisUser(MainLayout);